import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { signalsAPI } from '../services/api'

interface Anomaly {
  type: string
  severity: number
  headline: string
  direction_symbol: string
  detail?: string
}

interface Signal {
  id: string
  symbol: string
  signal_type: string
  anomaly_score: number
  generated_at: string
  details: {
    spot: number
    atm_iv: number
    pcr: number
    anomalies: Anomaly[]
    strategy_hint?: string
  }
}

interface AccuracyData {
  [key: string]: {
    total_signals: number
    moved_1pct_30m: number
    moved_2pct_30m: number
    avg_move_30m: number
  }
}

type Tab = 'live' | 'history' | 'accuracy'

const getStyles = (isMobile: boolean): Record<string, React.CSSProperties> => ({
  container: {
    background: '#0a0e1a',
    minHeight: '100vh',
    color: '#fff',
    overflowX: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: isMobile ? '12px 16px' : '16px 24px',
    borderBottom: '1px solid #1f2937',
    background: '#0d1117',
  },
  logo: { color: '#00d4ff', fontSize: isMobile ? 16 : 20, fontWeight: 700 },
  planBadge: {
    marginLeft: 8,
    background: '#1f2937',
    color: '#00d4ff',
    padding: '2px 8px',
    borderRadius: 20,
    fontSize: 10,
    fontWeight: 700,
  },
  headerRight: { display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 16 },
  updateTime: {
    color: '#4b5563',
    fontSize: 11,
    display: isMobile ? 'none' : 'block',
  },
  logoutBtn: {
    background: 'transparent',
    color: '#6b7280',
    border: '1px solid #374151',
    padding: isMobile ? '4px 10px' : '6px 16px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 12,
  },
  statsBar: {
    display: 'flex',
    borderBottom: '1px solid #1f2937',
    background: '#0d1117',
    overflowX: 'auto',
  },
  stat: {
    flex: 1,
    padding: isMobile ? '12px 8px' : '16px 24px',
    textAlign: 'center',
    borderRight: '1px solid #1f2937',
    minWidth: isMobile ? 70 : 'auto',
  },
  statValue: {
    display: 'block',
    fontSize: isMobile ? 18 : 24,
    fontWeight: 700,
    color: '#00d4ff',
  },
  statLabel: {
    display: 'block',
    fontSize: isMobile ? 9 : 11,
    color: '#6b7280',
    marginTop: 2,
  },
  tabs: {
    display: 'flex',
    padding: isMobile ? '0 8px' : '0 24px',
    borderBottom: '1px solid #1f2937',
    background: '#0d1117',
    overflowX: 'auto',
  },
  tab: {
    padding: isMobile ? '12px 12px' : '14px 24px',
    background: 'transparent',
    border: 'none',
    color: '#6b7280',
    cursor: 'pointer',
    fontSize: isMobile ? 12 : 14,
    borderBottom: '2px solid transparent',
    whiteSpace: 'nowrap' as const,
  },
  tabActive: { color: '#00d4ff', borderBottom: '2px solid #00d4ff' },
  content: { padding: isMobile ? 12 : 24 },
  grid: {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: isMobile ? 12 : 16,
  },
  card: {
    background: '#111827',
    border: '1px solid #1f2937',
    borderRadius: 12,
    padding: isMobile ? 16 : 20,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  symbol: { fontSize: isMobile ? 18 : 20, fontWeight: 700, color: '#fff' },
  scoreBadge: {
    padding: '4px 12px',
    borderRadius: 20,
    fontSize: 13,
    fontWeight: 700,
    color: '#000',
  },
  cardMeta: {
    display: 'flex',
    gap: isMobile ? 8 : 12,
    marginBottom: 16,
    flexWrap: 'wrap' as const,
  },
  metaItem: { color: '#6b7280', fontSize: isMobile ? 12 : 13 },
  anomalyRow: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
    padding: '10px 0',
    borderTop: '1px solid #1f2937',
  },
  dirSymbol: { fontSize: isMobile ? 16 : 20, minWidth: 20 },
  anomalyType: { color: '#00d4ff', fontSize: 11, fontWeight: 700, marginBottom: 2 },
  anomalyHeadline: { color: '#e5e7eb', fontSize: isMobile ? 12 : 13 },
  anomalyDetail: { color: '#6b7280', fontSize: 11, marginTop: 4 },
  hint: {
    background: '#1f2937',
    borderRadius: 8,
    padding: '10px 12px',
    fontSize: isMobile ? 12 : 13,
    color: '#9ca3af',
    marginTop: 12,
  },
  timestamp: { color: '#374151', fontSize: 11, marginTop: 12 },
  empty: {
    textAlign: 'center' as const,
    padding: '60px 20px',
    color: '#6b7280',
    fontSize: 16,
  },
  emptyIcon: { fontSize: 48, marginBottom: 16 },
  emptyHint: { fontSize: 13, color: '#4b5563', marginTop: 8 },
  locked: { textAlign: 'center' as const, padding: '60px 20px' },
  lockIcon: { fontSize: 48, marginBottom: 16 },
  lockTitle: { fontSize: 20, fontWeight: 700, color: '#fff', marginBottom: 8 },
  lockText: { color: '#6b7280', marginBottom: 24 },
  upgradeBtn: {
    background: '#00d4ff',
    color: '#000',
    border: 'none',
    padding: '12px 32px',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
  },
  historyControls: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
    flexWrap: 'wrap' as const,
  },
  historyLabel: { color: '#6b7280', fontSize: 13 },
  dayBtn: {
    background: '#1f2937',
    color: '#6b7280',
    border: '1px solid #374151',
    padding: '4px 12px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 12,
  },
  dayBtnActive: {
    background: '#00d4ff',
    color: '#000',
    border: '1px solid #00d4ff',
  },
  historyTable: {
    background: '#111827',
    borderRadius: 12,
    overflow: 'hidden',
    overflowX: 'auto' as const,
  },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: isMobile ? '75px 85px 1fr 55px' : '120px 120px 1fr 80px 100px 80px',
    padding: '12px 16px',
    background: '#1f2937',
    gap: 8,
  },
  th: {
    color: '#6b7280',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: isMobile ? '75px 85px 1fr 55px' : '120px 120px 1fr 80px 100px 80px',
    padding: '12px 16px',
    borderTop: '1px solid #1f2937',
    gap: 8,
    alignItems: 'center',
  },
  td: { color: '#e5e7eb', fontSize: isMobile ? 12 : 13 },
  accuracyGrid: {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 16,
  },
  accuracyCard: {
    background: '#111827',
    border: '1px solid #1f2937',
    borderRadius: 12,
    padding: 20,
  },
  accuracyType: { color: '#00d4ff', fontWeight: 700, fontSize: 14, marginBottom: 16 },
  accuracyStats: { display: 'flex', justifyContent: 'space-between', marginBottom: 16 },
  accuracyStat: { textAlign: 'center' as const },
  accuracyValue: { display: 'block', fontSize: 24, fontWeight: 700, color: '#fff' },
  accuracyLabel: { display: 'block', fontSize: 11, color: '#6b7280', marginTop: 4 },
  progressBar: { height: 6, background: '#1f2937', borderRadius: 3, overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 3, transition: 'width 0.3s ease' },
  disclaimer: {
    textAlign: 'center' as const,
    padding: '16px',
    color: '#374151',
    fontSize: 11,
    borderTop: '1px solid #1f2937',
  },
})

export default function Dashboard() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [signals, setSignals] = useState<Signal[]>([])
  const [history, setHistory] = useState<Signal[]>([])
  const [accuracy, setAccuracy] = useState<AccuracyData>({})
  const [plan, setPlan] = useState('basic')
  const [nextScan, setNextScan] = useState('')
  const [loading, setLoading] = useState(true)
  const [lastUpdate, setLastUpdate] = useState('')
  const [activeTab, setActiveTab] = useState<Tab>('live')
  const [historyDays, setHistoryDays] = useState(5)
  const navigate = useNavigate()

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const s = getStyles(isMobile)

  const fetchSignals = async () => {
    try {
      const res = await signalsAPI.getLatest()
      setSignals(res.data.signals || [])
      setPlan(res.data.plan || 'basic')
      setNextScan(res.data.next_scan || '')
      setLastUpdate(new Date().toLocaleTimeString('en-IN'))
    } catch (err) {
      console.error('Failed to fetch signals', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchHistory = async (days: number = 5) => {
    try {
      const res = await signalsAPI.getHistory(days)
      setHistory(res.data.signals || [])
    } catch (err) {
      console.error('Failed to fetch history', err)
    }
  }

  const fetchAccuracy = async () => {
    try {
      const res = await signalsAPI.getAccuracy()
      setAccuracy(res.data.accuracy || {})
    } catch (err) {
      console.error('Failed to fetch accuracy', err)
    }
  }

  useEffect(() => {
    fetchSignals()
    const interval = setInterval(fetchSignals, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (activeTab === 'history') fetchHistory(historyDays)
    if (activeTab === 'accuracy') fetchAccuracy()
  }, [activeTab, historyDays])

  const logout = () => {
    localStorage.clear()
    navigate('/login')
  }

  const severityColor = (score: number) => {
    if (score >= 7) return '#ef4444'
    if (score >= 4) return '#f59e0b'
    return '#00d4ff'
  }

  const scoreDisplay = (score: number) => `${Math.min(score, 10)}/10`

  return (
    <div style={s.container}>
      {/* Header */}
      <div style={s.header}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={s.logo}>AITradeMinds</span>
          <span style={s.planBadge}>{plan.toUpperCase()}</span>
        </div>
        <div style={s.headerRight}>
          {lastUpdate && <span style={s.updateTime}>Updated {lastUpdate}</span>}
          <button style={s.logoutBtn} onClick={logout}>Logout</button>
        </div>
      </div>

      {/* Stats bar */}
      <div style={s.statsBar}>
        <div style={s.stat}>
          <span style={s.statValue}>{signals.length}</span>
          <span style={s.statLabel}>Anomalies</span>
        </div>
        <div style={s.stat}>
          <span style={s.statValue}>203</span>
          <span style={s.statLabel}>Scanned</span>
        </div>
        <div style={s.stat}>
          <span style={s.statValue}>{nextScan || 'Closed'}</span>
          <span style={s.statLabel}>Next Scan</span>
        </div>
        <div style={s.stat}>
          <span style={s.statValue}>15m</span>
          <span style={s.statLabel}>Interval</span>
        </div>
      </div>

      {/* Tabs */}
      <div style={s.tabs}>
        {(['live', 'history', 'accuracy'] as Tab[]).map(tab => (
          <button
            key={tab}
            style={{ ...s.tab, ...(activeTab === tab ? s.tabActive : {}) }}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'live' && (isMobile ? '📡 Live' : '📡 Live Signals')}
            {tab === 'history' && (isMobile ? '📋 History' : '📋 History')}
            {tab === 'accuracy' && (isMobile ? '🎯 Accuracy' : '🎯 Accuracy')}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={s.content}>

        {/* LIVE TAB */}
        {activeTab === 'live' && (
          loading ? (
            <div style={s.empty}>Loading signals...</div>
          ) : signals.length === 0 ? (
            <div style={s.empty}>
              <div style={s.emptyIcon}>📊</div>
              <div>No anomalies detected in this scan</div>
              <div style={s.emptyHint}>Market may be low-conviction or pipeline warming up</div>
              <div style={s.emptyHint}>Next scan at {nextScan}</div>
            </div>
          ) : (
            <div style={s.grid}>
              {signals.map(signal => (
                <div key={signal.id} style={s.card}>
                  <div style={s.cardHeader}>
                    <span style={s.symbol}>{signal.symbol}</span>
                    <span style={{
                      ...s.scoreBadge,
                      background: severityColor(signal.anomaly_score),
                    }}>
                      {scoreDisplay(signal.anomaly_score)}
                    </span>
                  </div>
                  <div style={s.cardMeta}>
                    {signal.details?.spot > 0 && (
                      <span style={s.metaItem}>
                        ₹{signal.details.spot.toLocaleString('en-IN')}
                      </span>
                    )}
                    {signal.details?.atm_iv > 0 && (
                      <span style={s.metaItem}>IV: {signal.details.atm_iv}%</span>
                    )}
                    {signal.details?.pcr > 0 && (
                      <span style={s.metaItem}>PCR: {signal.details.pcr}</span>
                    )}
                  </div>
                  {signal.details?.anomalies?.map((a, i) => (
                    <div key={i} style={s.anomalyRow}>
                      <span style={s.dirSymbol}>{a.direction_symbol}</span>
                      <div>
                        <div style={s.anomalyType}>{a.type}</div>
                        <div style={s.anomalyHeadline}>{a.headline}</div>
                        {a.detail && (
                          <div style={s.anomalyDetail}>{a.detail}</div>
                        )}
                      </div>
                    </div>
                  ))}
                  {signal.details?.strategy_hint && (
                    <div style={s.hint}>💡 {signal.details.strategy_hint}</div>
                  )}
                  <div style={s.timestamp}>
                    {new Date(signal.generated_at).toLocaleTimeString('en-IN')}
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {/* HISTORY TAB */}
        {activeTab === 'history' && (
          plan === 'basic' ? (
            <div style={s.locked}>
              <div style={s.lockIcon}>🔒</div>
              <div style={s.lockTitle}>Pro Feature</div>
              <div style={s.lockText}>Signal history requires Pro or Elite plan</div>
              <button style={s.upgradeBtn}>Upgrade to Pro — ₹2,499/month</button>
              <button style={s.upgradeBtn} onClick={() => navigate('/upgrade')}> Upgrade to Pro — ₹2,499/month
              </button>
            </div>
          ) : (
            <>
              <div style={s.historyControls}>
                <span style={s.historyLabel}>Show last:</span>
                {[1, 3, 5].map(d => (
                  <button
                    key={d}
                    style={{
                      ...s.dayBtn,
                      ...(historyDays === d ? s.dayBtnActive : {})
                    }}
                    onClick={() => setHistoryDays(d)}
                  >
                    {d}d
                  </button>
                ))}
              </div>
              {history.length === 0 ? (
                <div style={s.empty}>
                  <div style={s.emptyIcon}>📋</div>
                  <div>No historical signals yet</div>
                  <div style={s.emptyHint}>History builds up during market hours</div>
                </div>
              ) : (
                <div style={s.historyTable}>
                  <div style={s.tableHeader}>
                    <span style={s.th}>Time</span>
                    <span style={s.th}>Symbol</span>
                    <span style={s.th}>Signal</span>
                    <span style={s.th}>Score</span>
                    {!isMobile && <span style={s.th}>Price</span>}
                    {!isMobile && <span style={s.th}>PCR</span>}
                  </div>
                  {history.map(h => (
                    <div key={h.id} style={s.tableRow}>
                      <span style={s.td}>
                        {new Date(h.generated_at).toLocaleTimeString('en-IN', {
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                      <span style={{ ...s.td, color: '#00d4ff', fontWeight: 700 }}>
                        {h.symbol}
                      </span>
                      <span style={{ ...s.td, fontSize: 11 }}>{h.signal_type}</span>
                      <span style={{ ...s.td, color: severityColor(h.anomaly_score) }}>
                        {scoreDisplay(h.anomaly_score)}
                      </span>
                      {!isMobile && (
                        <span style={s.td}>
                          ₹{h.details?.spot?.toLocaleString('en-IN') || '-'}
                        </span>
                      )}
                      {!isMobile && (
                        <span style={s.td}>{h.details?.pcr || '-'}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )
        )}

        {/* ACCURACY TAB */}
        {activeTab === 'accuracy' && (
          plan === 'basic' ? (
            <div style={s.locked}>
              <div style={s.lockIcon}>🔒</div>
              <div style={s.lockTitle}>Pro Feature</div>
              <div style={s.lockText}>Accuracy analytics requires Pro or Elite plan</div>
              <button style={s.upgradeBtn}>Upgrade to Pro — ₹2,499/month</button>
            </div>
          ) : Object.keys(accuracy).length === 0 ? (
            <div style={s.empty}>
              <div style={s.emptyIcon}>🎯</div>
              <div>Accuracy data building up</div>
              <div style={s.emptyHint}>
                Needs at least one full market day of outcome tracking
              </div>
            </div>
          ) : (
            <div style={s.accuracyGrid}>
              {Object.entries(accuracy).map(([type, stats]) => (
                <div key={type} style={s.accuracyCard}>
                  <div style={s.accuracyType}>{type}</div>
                  <div style={s.accuracyStats}>
                    <div style={s.accuracyStat}>
                      <span style={s.accuracyValue}>{stats.total_signals}</span>
                      <span style={s.accuracyLabel}>Total</span>
                    </div>
                    <div style={s.accuracyStat}>
                      <span style={{
                        ...s.accuracyValue,
                        color: stats.moved_2pct_30m >= 50 ? '#22c55e' : '#f59e0b',
                        fontSize: 20,
                      }}>
                        {stats.moved_2pct_30m}%
                      </span>
                      <span style={s.accuracyLabel}>Moved 2%+ in 30m</span>
                    </div>
                    <div style={s.accuracyStat}>
                      <span style={{ ...s.accuracyValue, fontSize: 20 }}>
                        {stats.avg_move_30m}%
                      </span>
                      <span style={s.accuracyLabel}>Avg Move</span>
                    </div>
                  </div>
                  <div style={s.progressBar}>
                    <div style={{
                      ...s.progressFill,
                      width: `${stats.moved_2pct_30m}%`,
                      background: stats.moved_2pct_30m >= 50 ? '#22c55e' : '#f59e0b',
                    }} />
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      <div style={s.disclaimer}>
        ⚠️ Research intelligence only. Not SEBI registered investment advice.
      </div>
    </div>
  )
}